import type { TRefElementInstance } from 'widgets/toolbox/RefElement';
import type { TProcessButtonInstance } from 'widgets/global/ProcessButton';
import type { TProduct } from 'core/product/CoreAddToCartMixin';
import type { TLabelInstance } from 'widgets/global/Label';

import { TPageLoadAction, PAGE_LOAD_ACTION_TYPES } from 'core/product/PageLoadActionProcessor';
import { isBase64Encoded, isMember, isSignedIn } from 'core/utils';

declare const window: ExtendedWindow;

const { contexts } = window;

/**
 * @description Base SignInToBuyMixin implementation
 * @param CoreProductDetail Base widget for extending
 * @returns Sign in to buy mixin widget
 */
function SignInToBuyMixinClassCreator(CoreProductDetail: import('core/product/CoreAddToCartMixin').TCoreAddToCartMixin) {
    class SignInToBuyMixin extends CoreProductDetail {
        prefs() {
            return {
                isSelectVariation: false,
                ...super.prefs()
            };
        }

        /**
         * @description Widget initialization
         */
        init(): void {
            super.init();

            this.renderSignInToBuyDetails();
        }

        /**
         * @description Get page load action object for sign in to buy
         */
        getPageLoadAction() {
            const isWishlistPage = contexts.includes('wishlist');

            let pageLoadAction;

            // Check that this is wishlist page and product is a variation group
            if (isWishlistPage && this.prefs().isSelectVariation) {
                pageLoadAction = {
                    type: PAGE_LOAD_ACTION_TYPES.WISHLIST_QUICK_VIEW,
                    data: { pid: this.prefs().currentProductId }
                };
            } else {
                this.getById(this.prefs().addToCartBtn, (addToCartButton: TProcessButtonInstance) => {
                    pageLoadAction = {
                        type: isWishlistPage ? PAGE_LOAD_ACTION_TYPES.WISHLIST_ADD_TO_CART : PAGE_LOAD_ACTION_TYPES.PDP_ADD_TO_CART,
                        data: this.buildAddToCartRequestData(addToCartButton)
                    };
                });
            }

            if (this.prefs().isLoyaltyEnrollmentRequired) {
                pageLoadAction = {
                    type: PAGE_LOAD_ACTION_TYPES.ENSURE_LOYALTY_ENROLLMENT,
                    data: { originalAction: pageLoadAction }
                };
            }

            return <TPageLoadAction>pageLoadAction;
        }

        /**
         * @description Handles sign in to buy click
         * @param button Button ref element instance
         */
        handleSignInToBuyClick(button: TRefElementInstance) {
            if (this.callIfExists('checkUnselectedAttributes') !== false) {
                const modalTitle = <string>button.data('modalTitle');
                const isEnrollLoyaltyToBuy = isSignedIn() && !isMember(this.prefs().isLoyaltyEnrollmentRequired);
                const modalURL = <string>(isEnrollLoyaltyToBuy ? button.data('enrollModalUrl') : button.data('modalUrl'));
                const modalEvent = <string>(isEnrollLoyaltyToBuy ? 'enrollLoyaltyToBuyModal.show' : 'signInToBuyModal.show');

                this.eventBus().emit(modalEvent, {
                    url: isBase64Encoded(modalURL) ? atob(modalURL) : modalURL,
                    headerText: modalTitle,
                    wrapperClasses: 'm-sign_in_to_buy',
                    pageLoadAction: this.getPageLoadAction()
                });
            }
        }

        /**
         * @description Render sign in to buy details component
         * @param product Product object
         */
        renderSignInToBuyDetails(product?: TProduct) {
            this.getById('signInToBuyDetails', (signInToBuyDetails: TLabelInstance) => {
                const prefs = this.prefs();
                const isMembersOnly = product ? product.membersOnly : prefs.isMembersOnly;
                const isSignInToBuy = isMembersOnly && !isMember(prefs.isLoyaltyEnrollmentRequired);
                const context = {
                    productAvailable: product ? product.available : this.prefs().isAvailable,
                    isSignInToBuy
                };

                return signInToBuyDetails.render('template', context, signInToBuyDetails.ref('container'));
            });
        }

        /**
         * @description Update Product View
         * @param product Product object
         */
        updateProductView(product: TProduct) {
            super.updateProductView(product);

            this.renderSignInToBuyDetails(product);
        }
    }

    return SignInToBuyMixin;
}

export type TSignInToBuyMixin = ReturnType<typeof SignInToBuyMixinClassCreator>;

export type TSignInToBuyMixinInstance = InstanceType<TSignInToBuyMixin>;

export default SignInToBuyMixinClassCreator;
